import { Paper, Typography, Box, LinearProgress, Card, Divider, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { MetricType } from "../../../types/dashboard";
import { formatCurrencyShort } from "../../../utils/format";
import { useDashboard } from "../../../contexts/DashboardContext";
import { MetricBarsSkeleton } from "./Skeletons";
import { DashboardSourceInfo } from "./DashboardSourceInfo";

interface MetricsByClientProps {
  metricType: MetricType;
  title: string;
  color: string;
}

const StyledPaper = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: 282,
}));

const ChartTitle = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
});

const MetricRow = styled(Box)({
  display: "flex",
  gap: "0.5rem",
  marginBottom: "0.75rem",
  paddingRight: 8,
  position: "relative",
});

const MetricName = styled(Typography)({
  fontSize: "0.75rem",
  color: "#3E3E3E",
  width: 80,
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
});

const MetricValue = styled(Typography)({
  fontSize: "0.675rem",
  color: "#222222",
  minWidth: 60,
  textAlign: "right",
});

const StyledLinearProgress = styled(LinearProgress)({
  height: 16,
  borderRadius: 4,
  backgroundColor: "transparent",
  flex: 1,
  "& .MuiLinearProgress-bar": {
    borderRadius: 4,
  },
});

export const MetricsByClient = ({ metricType, title, color }: MetricsByClientProps) => {
  const {
    initialEventControlRankings,
    filteredEventControlRankings,
    loading,
    currentFilters,
    toggleClientFilter,
    hasFilters,
  } = useDashboard();

  const rankingKey = metricType === MetricType.BILLING ? "billingByClient" : "revenueByClient";
  const effectiveInitial = initialEventControlRankings ?? filteredEventControlRankings;
  const initialData = effectiveInitial?.[rankingKey] ?? [];
  const filteredData = hasFilters
    ? (filteredEventControlRankings?.[rankingKey] ?? [])
    : initialData;

  const combinedData = initialData.map((item) => {
    const filtered = filteredData.find((f) => f.id === item.id);
    return {
      clientId: item.id,
      clientName: item.name,
      filteredValue: filtered?.value ?? 0,
      filteredPercentage: filtered?.percentage ?? 0,
    };
  });

  return (
    <StyledPaper>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ChartTitle>{title}</ChartTitle>
          <DashboardSourceInfo context={title} />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <MetricBarsSkeleton />
      ) : (
        <Box sx={{ overflowY: "auto", maxHeight: 240 }}>
          {combinedData
            .sort((a, b) => b.filteredValue - a.filteredValue)
            .map((item) => (
              <MetricRow
                key={item.clientId}
                onClick={() => toggleClientFilter(item.clientId)}
                sx={{
                  cursor: "pointer",
                  bgcolor: currentFilters.clientId === item.clientId ? `${color}10` : "transparent",
                  "&:hover": {
                    bgcolor: `${color}20`,
                  },
                }}
              >
                <MetricName>{item.clientName}</MetricName>

                <Box position="relative" flex={1}>
                  <StyledLinearProgress
                    variant="determinate"
                    value={item.filteredPercentage}
                    sx={{
                      backgroundColor: hasFilters ? `${color}20` : "transparent",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: color,
                      },
                    }}
                  />
                </Box>

                <MetricValue>{formatCurrencyShort(item.filteredValue)}</MetricValue>
              </MetricRow>
            ))}
        </Box>
      )}
    </StyledPaper>
  );
};
